import { useMemo, useState } from "react";
import { useLang } from "../lib/useLang";
import { addReview, getReviews, ratingSummary } from "../data/reviews";

export function Stars({
  value,
  onChange,
}: {
  value: number;
  onChange?: (v: number) => void;
}) {
  return (
    <div className="flex" aria-label={`${value} / 5`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <button
          type="button"
          key={i}
          disabled={!onChange}
          onClick={() => onChange?.(i)}
          className={`text-lg leading-none ${i <= value ? "text-gold" : "text-black/20"} ${
            onChange ? "cursor-pointer" : "cursor-default"
          }`}
        >
          ★
        </button>
      ))}
    </div>
  );
}

export function ReviewsSection() {
  const { t, pick } = useLang();
  const [reviews, setReviews] = useState(getReviews());
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [rating, setRating] = useState(5);
  const [text, setText] = useState("");
  const [done, setDone] = useState(false);
  const summary = useMemo(() => ratingSummary(), [reviews]);

  function submit() {
    if (!text.trim()) return;
    addReview({ name, rating, text });
    setReviews(getReviews());
    setDone(true);
    setOpen(false);
    setName("");
    setText("");
    setRating(5);
  }

  return (
    <section className="pt-8">
      <div className="mb-3 flex items-end justify-between gap-2">
        <div>
          <h2 className="display text-2xl text-ink">{t("reviews.title")}</h2>
          <div className="mt-1 flex items-center gap-2">
            <Stars value={Math.round(summary.avg)} />
            <span className="font-bold text-ink">{summary.avg.toFixed(1)}</span>
            <span className="text-sm text-black/50">
              · {t("reviews.count", { count: summary.count })}
            </span>
          </div>
        </div>
        <button className="btn-outline shrink-0 text-sm" onClick={() => setOpen((o) => !o)}>
          {t("reviews.leave")}
        </button>
      </div>

      {done && (
        <p className="mb-3 rounded-xl bg-accent/10 p-2.5 text-sm font-semibold text-accent-dark">
          ✔ {t("reviews.thanks")}
        </p>
      )}

      {open && (
        <div className="card mb-4 space-y-3 p-4">
          <input
            className="input"
            placeholder={t("reviews.yourName")}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <div>
            <span className="mb-1 block text-xs font-semibold text-black/55">
              {t("reviews.rate")}
            </span>
            <Stars value={rating} onChange={setRating} />
          </div>
          <textarea
            className="input"
            rows={2}
            placeholder={t("reviews.yourReview")}
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
          <button className="btn-primary w-full" onClick={submit} disabled={!text.trim()}>
            {t("reviews.submit")}
          </button>
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        {reviews.slice(0, 6).map((r) => (
          <div key={r.id} className="card p-4">
            <div className="flex items-center justify-between">
              <span className="font-semibold text-ink">{r.name}</span>
              <Stars value={r.rating} />
            </div>
            <p className="mt-1 text-sm leading-relaxed text-black/60" dir="auto">
              {pick(r.text, r.textAr || r.text)}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
}
